import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION || '1.0.0';

/**
 * Initialize Sentry error tracking
 * DSN should be set via VITE_SENTRY_DSN environment variable
 */
export function initSentry() {
  if (!SENTRY_DSN) {
    console.debug('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: `wasser-pro-client@${RELEASE}`,

    // Performance Monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Ad blockers
      /googlesyndication/,
      /doubleclick/,
      // Network errors (user-side)
      'Network Error',
      'Failed to fetch',
      'Load failed',
      'NetworkError',
      // Cancel errors
      'AbortError',
      'The operation was aborted',
      // React dev mode
      'ResizeObserver loop',
      // Benign errors
      'Non-Error promise rejection captured',
    ],

    // Don't send errors from local development
    enabled: ENVIRONMENT !== 'development',

    // Before send hook for data scrubbing
    beforeSend(event, _hint) {
      // Remove sensitive data
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }

      // Add user context if available
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          Sentry.setUser({
            id: userData.id,
            email: userData.email,
            // Don't include full name for privacy
          });
        } catch {
          // Invalid JSON, skip
        }
      }

      return event;
    },

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: false,
        // Block all media
        blockAllMedia: false,
      }),
    ],
  });

  console.debug('[Sentry] Initialized for', ENVIRONMENT);
}

/**
 * Capture exception with additional context
 */
export function captureException(error, context = {}) {
  if (!SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context.extra) {
      Object.entries(context.extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    if (context.level) {
      scope.setLevel(context.level);
    }

    Sentry.captureException(error);
  });
}

/**
 * Capture message with severity level
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (!SENTRY_DSN) return;

  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (context.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    Sentry.captureMessage(message);
  });
}

/**
 * Set user context for all future events
 */
export function setUser(user) {
  if (!SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message, category = 'custom', data = {}) {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Export Sentry for direct access if needed
export { Sentry };
